import { useState, useMemo, Fragment } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Calendar, ChevronLeft, ChevronRight, Loader2, ClipboardList, Factory,
  X as XIcon, GripVertical, Package2, Pencil,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { getProductionPlan, lockSkuToSlot, unlockSlot } from "@/lib/api";

// ---------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------
const startOfWeek = (d) => {
  const nd = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = nd.getDay();
  nd.setDate(nd.getDate() + (dow === 0 ? -6 : 1 - dow));
  return nd;
};
const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const shortDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")} ${d.toLocaleString("en-US", { month: "short" })}`;
};

const daysUntil = (isoDate) => {
  if (!isoDate) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(isoDate); d.setHours(0, 0, 0, 0);
  return Math.round((d - today) / 86400000);
};
const urgencyClass = (isoDate) => {
  const d = daysUntil(isoDate);
  if (d === null) return "border-slate-300 bg-white";
  if (d < 0) return "border-red-500 bg-red-50";
  if (d <= 3) return "border-red-300 bg-red-50";
  if (d <= 7) return "border-amber-300 bg-amber-50";
  return "border-emerald-300 bg-emerald-50";
};

// SKU chip palette — colour per SKU-name-hash so eye can track it across board
const SKU_PALETTE = [
  "bg-sky-100 text-sky-900 border-sky-300",
  "bg-emerald-100 text-emerald-900 border-emerald-300",
  "bg-amber-100 text-amber-900 border-amber-300",
  "bg-fuchsia-100 text-fuchsia-900 border-fuchsia-300",
  "bg-indigo-100 text-indigo-900 border-indigo-300",
  "bg-rose-100 text-rose-900 border-rose-300",
  "bg-lime-100 text-lime-900 border-lime-300",
  "bg-cyan-100 text-cyan-900 border-cyan-300",
  "bg-orange-100 text-orange-900 border-orange-300",
  "bg-violet-100 text-violet-900 border-violet-300",
];
const skuColor = (sku) => {
  let h = 0;
  for (let i = 0; i < (sku || "").length; i++) h = ((h << 5) - h + sku.charCodeAt(i)) | 0;
  return SKU_PALETTE[Math.abs(h) % SKU_PALETTE.length];
};

// ---------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------
const ProductionPlanning = () => {
  const qc = useQueryClient();
  const [weekStart, setWeekStart] = useState(() => toISO(startOfWeek(new Date())));
  const [dragSkuName, setDragSkuName] = useState(null);
  const [hoverCell, setHoverCell] = useState(null);  // "press_id::date"
  const [editSlot, setEditSlot] = useState(null);   // slot object being edited

  const { data: board, isLoading } = useQuery({
    queryKey: ["prod-plan", weekStart],
    queryFn: () => getProductionPlan(weekStart),
    refetchInterval: 10000,
  });

  const weekDays = board?.week_days || [];
  const presses = board?.presses || [];
  const orders = board?.orders || [];
  const skuDemand = board?.sku_demand || [];
  const slots = board?.slots || [];
  const perDayBySku = board?.per_day_by_sku || {};

  // Map (press_id, date) → LIST of slots (multiple SKUs allowed per cell)
  const slotsByCell = useMemo(() => {
    const map = {};
    for (const s of slots) {
      const key = `${s.press_id}::${s.date}`;
      (map[key] = map[key] || []).push(s);
    }
    return map;
  }, [slots]);

  const lockMut = useMutation({
    mutationFn: lockSkuToSlot,
    onSuccess: (_res, vars) => {
      toast.success(`${vars.sku_name} · ${vars.qty_tons}T locked`);
      qc.invalidateQueries({ queryKey: ["prod-plan"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Lock failed"),
  });
  const unlockMut = useMutation({
    mutationFn: unlockSlot,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["prod-plan"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Unlock failed"),
  });

  const onCellDrop = (pressId, date, e) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData("application/json");
    if (!raw) return;
    let sku;
    try { sku = JSON.parse(raw); } catch { return; }
    setDragSkuName(null); setHoverCell(null);
    const answer = window.prompt(`Target production for ${sku.sku_name} on ${date}?\n\nEnter tonnage (Tons):`, "1");
    if (answer === null) return;                // cancelled
    const tons = parseFloat(answer);
    if (!(tons > 0)) {
      toast.error("Enter a positive number of tons");
      return;
    }
    lockMut.mutate({
      press_id: pressId,
      date,
      sku_name: sku.sku_name,
      sku_id: sku.sku_id || "",
      qty_tons: tons,
    });
  };

  const shiftWeek = (delta) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + delta * 7);
    setWeekStart(toISO(startOfWeek(d)));
  };
  const weekLabel = useMemo(() => {
    if (!weekDays.length) return "";
    return `${shortDate(weekDays[0])} – ${shortDate(weekDays[6])}`;
  }, [weekDays]);

  // "DD/MM to DD/MM" range for the "Total Production Planned" strip caption.
  // Uses only day + month so the label stays compact regardless of screen width.
  const ddmm = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
  };
  const plannedRangeLabel = useMemo(() => {
    if (!weekDays.length) return "";
    return `${ddmm(weekDays[0])} to ${ddmm(weekDays[6])}`;
  }, [weekDays]);

  const totalPlannedTons = useMemo(() => slots.reduce((s, x) => s + Number(x.qty_tons || 0), 0), [slots]);

  return (
    <div data-testid="production-planning-page" className="max-w-[1700px] mx-auto space-y-3">
      {/* Header — week nav */}
      <div className="border-b border-slate-200 pb-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Calendar className="h-6 w-6 text-slate-900" />
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-slate-500 font-semibold">Plan</p>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Production Planning</h1>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <p className="text-[9px] tracking-[0.28em] uppercase text-slate-500 font-semibold">
            Week &middot; Monday to Sunday
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" data-testid="prev-week-btn" onClick={() => shiftWeek(-1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div
              className="px-3 h-9 border border-slate-300 rounded-sm bg-white flex items-center text-sm font-mono min-w-[170px] justify-center"
              data-testid="week-label"
              title={weekDays.length ? `Mon ${weekDays[0]} → Sun ${weekDays[6]}` : ""}
            >
              {weekLabel || "—"}
            </div>
            <Button variant="outline" size="sm" data-testid="next-week-btn" onClick={() => shiftWeek(1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" data-testid="this-week-btn" onClick={() => setWeekStart(toISO(startOfWeek(new Date())))}>This week</Button>
          </div>
        </div>
      </div>

      {/* Top strip — SKU chips + per-day tonnage summary */}
      <div data-testid="sku-demand-strip" className="bg-slate-900 rounded-sm p-3 text-white">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3 flex-wrap">
            <Package2 className="h-4 w-4 text-slate-300" />
            <p className="text-[11px] tracking-[0.25em] uppercase text-slate-200 font-semibold">
              Lamination Production Planning Sheet
            </p>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-[11px] tracking-widest uppercase text-slate-300 font-semibold">
              Total production planned from <span className="font-mono text-white">{plannedRangeLabel}</span>:
            </span>
            <span
              className="text-lg font-bold font-mono text-emerald-300"
              data-testid="total-planned-tons"
              title="Live sum of all SKU chips locked in the week — grows as you drop more chips"
            >
              {totalPlannedTons.toLocaleString(undefined, { maximumFractionDigits: 1 })} T
            </span>
          </div>
        </div>
        <p className="text-[10px] text-slate-400 flex items-center gap-1 mt-1"><GripVertical className="h-3 w-3" /> Drag a SKU chip onto a press cell &amp; enter tons to lock it in</p>

        <div className="h-px bg-white/25 my-3" />

        {skuDemand.length === 0 ? (
          <p className="text-xs text-slate-400 italic py-2">Add FG SKUs in Masters to see chips here.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {skuDemand.map((s) => {
              const clr = skuColor(s.sku_name);
              return (
                <div
                  key={s.sku_name}
                  data-testid={`sku-chip-${s.sku_name}`}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData("application/json", JSON.stringify({ sku_name: s.sku_name, sku_id: s.sku_id }));
                    e.dataTransfer.effectAllowed = "copy";
                    setDragSkuName(s.sku_name);
                  }}
                  onDragEnd={() => { setDragSkuName(null); setHoverCell(null); }}
                  className={`w-[160px] cursor-grab active:cursor-grabbing border-2 rounded-sm px-2 py-1.5 ${clr} shadow-sm hover:shadow-md transition-shadow flex flex-col gap-0.5`}
                >
                  <div className="flex items-center gap-1.5">
                    <GripVertical className="h-3.5 w-3.5 opacity-60" />
                    <span className="font-heading font-bold text-base tracking-tight leading-tight truncate">{s.sku_name}</span>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <span className="text-[10px] uppercase tracking-widest opacity-70">live stock</span>
                    <span className="font-mono font-bold text-sm">
                      {Math.round(s.live_stock_kg || 0).toLocaleString()}
                      <span className="text-[10px] ml-0.5 opacity-70">kg</span>
                    </span>
                  </div>
                  {Number(s.total_kg) > 0 && (
                    <div className="flex items-baseline justify-between">
                      <span className="text-[10px] uppercase tracking-widest opacity-60">order demand</span>
                      <span className="font-mono text-xs opacity-80">
                        {Math.round(s.total_kg).toLocaleString()}<span className="text-[9px] ml-0.5">kg</span>
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Per-day per-SKU tonnage summary — replaces the old totals row */}
        {weekDays.length > 0 && (
          <>
            <div className="h-px bg-white/25 mt-3 mb-2" />
            <div className="grid grid-cols-7 gap-2" data-testid="per-day-summary">
              {weekDays.map((d, i) => {
                const skuTotals = perDayBySku[d] || {};
                const dayTotal = Object.values(skuTotals).reduce((a, b) => a + Number(b || 0), 0);
                const entries = Object.entries(skuTotals).sort((a, b) => Number(b[1]) - Number(a[1]));
                return (
                  <div key={d} className="text-[10px] leading-tight" data-testid={`day-summary-${d}`}>
                    <div className="flex items-baseline justify-between border-b border-white/10 pb-0.5 mb-0.5">
                      <span className="uppercase tracking-widest text-slate-400 font-semibold">
                        {DAY_LABELS[i]} <span className="font-mono opacity-70">{d.slice(5)}</span>
                      </span>
                      <span className="font-mono text-emerald-300 font-bold">
                        {dayTotal ? `${dayTotal.toFixed(dayTotal >= 10 ? 0 : 1)}T` : "—"}
                      </span>
                    </div>
                    {entries.length === 0 ? (
                      <p className="text-slate-500 italic">nothing planned</p>
                    ) : entries.map(([sku, tons]) => (
                      <div key={sku} className="flex items-baseline justify-between gap-1">
                        <span className="truncate text-slate-200">{sku}</span>
                        <span className="font-mono text-slate-300 shrink-0">{Number(tons).toFixed(Number(tons) >= 10 ? 0 : 1)}T</span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : (
        <div className="grid grid-cols-[220px_1fr] gap-3">
          {/* LEFT — FG orders */}
          <div data-testid="fg-orders-panel" className="bg-white border border-slate-200 rounded-sm">
            <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ClipboardList className="h-4 w-4 text-slate-700" />
                <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">FG Orders</p>
              </div>
              <span className="text-xs font-mono text-slate-500" data-testid="fg-orders-count">{orders.length}</span>
            </div>
            <div className="max-h-[75vh] overflow-y-auto p-2 space-y-2">
              {orders.length === 0 ? (
                <p className="text-xs text-slate-500 italic p-2">No open FG orders.</p>
              ) : orders.map((ln) => (
                <div
                  key={`${ln.so_id}::${ln.line_id}`}
                  data-testid={`order-card-${ln.line_id}`}
                  className={`border-2 rounded-sm p-2 ${urgencyClass(ln.target_dispatch_date)}`}
                >
                  <div className="flex items-baseline justify-between gap-1">
                    <p className="font-heading font-bold text-sm text-slate-900 leading-tight truncate">{ln.customer || "—"}</p>
                    <span className="font-mono text-[9px] text-slate-500 shrink-0">{ln.order_number}</span>
                  </div>
                  <div className="mt-1 flex items-baseline justify-between gap-1">
                    <span className={`font-heading font-bold text-xs tracking-tight px-1.5 py-0.5 border rounded-sm ${skuColor(ln.sku_name)}`}>{ln.sku_name || "—"}</span>
                    <div className="text-right">
                      <p className="font-heading font-bold text-base text-slate-900 leading-none">{Math.round(ln.balance_kg).toLocaleString()}</p>
                      <p className="text-[8px] tracking-[0.2em] uppercase text-slate-500 font-semibold">kg pending</p>
                    </div>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-[10px] text-slate-600">
                    <span>due <b className="font-mono">{ln.target_dispatch_date || "—"}</b></span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CENTER — Board grid Mon-Sun */}
          <div data-testid="plan-board" className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
            <div
              className="grid gap-px bg-slate-200 min-w-[900px]"
              style={{ gridTemplateColumns: `minmax(110px, 130px) repeat(7, minmax(120px, 1fr))` }}
            >
              <div className="bg-slate-50 px-2 py-2 text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold flex items-center gap-1.5">
                <Factory className="h-3.5 w-3.5" /> Press ↓ / Day →
              </div>
              {weekDays.map((d, i) => (
                <div key={d} className="bg-slate-50 px-2 py-2 text-center">
                  <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">{DAY_LABELS[i]}</p>
                  <p className="text-xs font-mono text-slate-700">{d.slice(5)}</p>
                </div>
              ))}

              {presses.length === 0 ? (
                <div className="col-span-8 bg-white p-6 text-center text-sm text-slate-500 italic">No presses defined.</div>
              ) : presses.map((p) => (
                <Fragment key={p.id}>
                  <div className="bg-white px-2 py-2 flex items-center gap-1.5">
                    <div className="h-6 w-6 bg-slate-900 text-white flex items-center justify-center rounded-sm text-[10px] font-bold">
                      {(p.code || p.name || "?").replace(/^press[-_ ]?/i, "").slice(0, 2) || "?"}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-slate-900 truncate">{p.code || p.name}</p>
                      <p className="text-[10px] text-slate-500 truncate">
                        {p.allowed_fg_sku_names?.length ? `${p.allowed_fg_sku_names.length} SKU${p.allowed_fg_sku_names.length === 1 ? "" : "s"}` : "any SKU"}
                      </p>
                    </div>
                  </div>
                  {weekDays.map((d) => {
                    const cellKey = `${p.id}::${d}`;
                    const cellSlots = slotsByCell[cellKey] || [];
                    const isHovered = hoverCell === cellKey && dragSkuName;
                    return (
                      <CalendarCell
                        key={cellKey}
                        cellKey={cellKey}
                        cellSlots={cellSlots}
                        isHovered={isHovered}
                        onDragEnter={() => { if (dragSkuName) setHoverCell(cellKey); }}
                        onDragLeave={(e) => {
                          if (!e.currentTarget.contains(e.relatedTarget)) {
                            setHoverCell((prev) => (prev === cellKey ? null : prev));
                          }
                        }}
                        onDrop={(e) => onCellDrop(p.id, d, e)}
                        onEdit={(slot) => setEditSlot(slot)}
                      />
                    );
                  })}
                </Fragment>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Edit / delete a single chip inside a cell */}
      {editSlot && (
        <ChipEditor
          slot={editSlot}
          onClose={() => setEditSlot(null)}
          onSaved={() => { setEditSlot(null); qc.invalidateQueries({ queryKey: ["prod-plan"] }); }}
          onDeleted={() => {
            unlockMut.mutate(editSlot.id, {
              onSuccess: () => { setEditSlot(null); },
            });
          }}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------
// One calendar cell — supports multiple SKU chips. Each chip has a pencil
// icon that opens the edit/delete dialog (no hover overlay).
// ---------------------------------------------------------------------
const CalendarCell = ({ cellKey, cellSlots, isHovered, onDragEnter, onDragLeave, onDrop, onEdit }) => {
  const count = cellSlots.length;
  const isCompact = count >= 3;
  return (
    <div
      data-testid={`cell-${cellKey}`}
      onDragEnter={onDragEnter}
      onDragOver={(e) => e.preventDefault()}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`relative bg-white p-1.5 h-[86px] border-t border-slate-100 transition-colors overflow-hidden ${
        isHovered ? "bg-emerald-100 outline outline-2 outline-emerald-500" : ""
      }`}
    >
      {count === 0 ? (
        <div className="w-full h-full flex items-center justify-center">
          <span className="text-[10px] text-slate-300 italic">drop a SKU</span>
        </div>
      ) : (
        <div className={`w-full h-full flex ${isCompact ? "flex-wrap gap-0.5 content-start" : "flex-col gap-1"}`}>
          {cellSlots.map((s) => (
            <div
              key={s.id}
              data-testid={`chip-${s.id}`}
              className={`${skuColor(s.sku_name)} border rounded-sm flex items-center justify-between gap-1 group ${
                isCompact ? "px-1 py-0.5 text-[10px] flex-1 min-w-[45%]" : "px-1.5 py-1 text-xs w-full"
              }`}
              title={`${s.sku_name}, ${s.qty_tons || 0}Ton`}
            >
              <span className="truncate font-heading font-bold leading-none">
                {isCompact ? s.sku_name.slice(0, 5) : s.sku_name}
                <span className="opacity-70 font-mono ml-1">{s.qty_tons ? `${s.qty_tons}T` : ""}</span>
              </span>
              <button
                data-testid={`edit-btn-${s.id}`}
                onClick={(e) => { e.stopPropagation(); onEdit(s); }}
                className="opacity-40 group-hover:opacity-100 hover:bg-white/60 rounded-sm p-0.5 transition-opacity"
                title="Edit or remove"
              >
                <Pencil className="h-2.5 w-2.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------
// Chip editor — change qty in tons or delete the chip entirely.
// ---------------------------------------------------------------------
const ChipEditor = ({ slot, onClose, onSaved, onDeleted }) => {
  const [tons, setTons] = useState(String(slot.qty_tons ?? ""));
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const n = parseFloat(tons);
    if (!(n > 0)) { toast.error("Enter a positive tonnage"); return; }
    setBusy(true);
    try {
      // Overwrite: delete existing chip, create a fresh one with the new qty.
      await unlockSlot(slot.id);
      await lockSkuToSlot({
        press_id: slot.press_id, date: slot.date,
        sku_name: slot.sku_name, sku_id: slot.sku_id || "", qty_tons: n,
      });
      toast.success(`Updated ${slot.sku_name} → ${n}T`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      data-testid="chip-editor"
    >
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm bg-white rounded-sm border border-slate-200 p-5 space-y-4">
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold">Plan chip</p>
          <h3 className="font-heading text-lg font-bold text-slate-900">{slot.sku_name}</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            {slot.press_id} · {slot.date}
          </p>
        </div>
        <div className="space-y-2">
          <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Target production (Tons)</label>
          <input
            data-testid="chip-editor-tons"
            type="number" step="any" value={tons}
            onChange={(e) => setTons(e.target.value)}
            className="w-full h-11 px-3 border border-slate-300 rounded-sm font-mono text-lg"
            autoFocus
          />
        </div>
        <div className="flex items-center justify-between gap-2 pt-1">
          <button
            data-testid="chip-editor-delete"
            onClick={() => { if (window.confirm(`Delete ${slot.sku_name} · ${slot.qty_tons}T from this cell?`)) onDeleted(); }}
            disabled={busy}
            className="text-sm text-red-700 hover:text-red-900 font-semibold inline-flex items-center gap-1"
          >
            <XIcon className="h-4 w-4" /> Delete chip
          </button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button data-testid="chip-editor-save" size="sm" onClick={save} disabled={busy} className="bg-slate-900 hover:bg-slate-800 text-white">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductionPlanning;
